import { toast } from '../../common_components/Toast/ToastUtils';
import XLSX from 'xlsx-js-style';
const headerStyle = {
    font: { bold: true, sz: 11, color: { rgb: '374151' } },
    fill: { fgColor: { rgb: 'E5E7EB' } },
    alignment: { horizontal: 'center', vertical: 'center' },
};
const titleStyle = {
    font: { bold: true, sz: 16, color: { rgb: '181D27' } },
    alignment: { horizontal: 'center', vertical: 'center' },
};
const sectionStyle = {
    font: { bold: true, sz: 12, color: { rgb: '181D27' } },
};
const summaryRowStyle = {
    font: { bold: true, sz: 11, color: { rgb: '181D27' } },
    fill: { fgColor: { rgb: 'F3F4F6' } },
};
// ═══════════════ HELPERS ═══════════════
const money = (v) => Math.round((v || 0) * 100) / 100;
const pct = (v) => `${(v || 0).toFixed(1)}%`;
/** 4980 -> "1h 23m", 3600 -> "1h", 1200 -> "20m" (same format as the table). */
const duration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0 && m > 0)
        return `${h}h ${m}m`;
    if (h > 0)
        return `${h}h`;
    return `${m}m`;
};
const taskHours = (seconds) => `${Number((seconds / 3600).toFixed(1))}hr`;
const dateText = (iso) => (iso ? iso.slice(0, 10) : 'Present');
const sheetFrom = (title, headers, rows, summaryRow) => {
    const numCols = headers.length;
    const sheetData = [
        [title, ...Array(numCols - 1).fill('')],
        Array(numCols).fill(''),
        headers,
        ...rows,
        ...(summaryRow ? [summaryRow] : []),
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    if (ws['A1'])
        ws['A1'].s = titleStyle;
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } }];
    for (let c = 0; c < numCols; c++) {
        const h = ws[XLSX.utils.encode_cell({ r: 2, c })];
        if (h)
            h.s = headerStyle;
        if (summaryRow) {
            const s = ws[XLSX.utils.encode_cell({ r: 3 + rows.length, c })];
            if (s)
                s.s = summaryRowStyle;
        }
    }
    ws['!cols'] = headers.map((h, c) => ({
        wch: Math.max(h.length + 2, 12, ...rows.map((r) => String(r[c] ?? '').length + 2).map((n) => Math.min(n, 60))),
    }));
    return ws;
};
// ═══════════════ EXPORT ═══════════════
export function exportEmployeeDetails(input) {
    if (!input) {
        toast.info('No data available to export');
        return;
    }
    try {
        const { name, month, compareMonth, cards, bonus, accounts, accountPerformance } = input;
        const workbook = XLSX.utils.book_new();
        const titleSuffix = `${name} - ${month}`;
        // ── Sheet 1: Summary (the 8 cards + Bonus Summary) ──
        const cardHeaders = compareMonth
            ? ['Metric', month, compareMonth, 'Change %']
            : ['Metric', month];
        const cardRows = cards.map((c) => compareMonth
            ? [c.title, c.value, c.compareValue ?? '-', c.changePercent === null ? '-' : `${c.changePercent}%`]
            : [c.title, c.value]);
        const summaryWs = sheetFrom(`Employee Performance - ${titleSuffix}`, cardHeaders, cardRows);
        if (bonus) {
            const startRow = 3 + cardRows.length + 1; // one blank row after the cards
            XLSX.utils.sheet_add_aoa(summaryWs, [
                ['Bonus Summary'],
                ['Target', 'Bonus per Hitting Goal', 'No. Of Account Beat', 'Total Bonus Value'],
                ...bonus.rows.map((r) => [r.target_label, money(r.bonus_amount), r.accounts_beat, money(r.total_bonus)]),
                ['Total', '', bonus.rows.reduce((s, r) => s + r.accounts_beat, 0), money(bonus.total_bonus)],
            ], { origin: { r: startRow, c: 0 } });
            const ref = (r, c) => XLSX.utils.encode_cell({ r, c });
            if (summaryWs[ref(startRow, 0)])
                summaryWs[ref(startRow, 0)].s = sectionStyle;
            for (let c = 0; c < 4; c++) {
                if (summaryWs[ref(startRow + 1, c)])
                    summaryWs[ref(startRow + 1, c)].s = headerStyle;
                const totalRef = ref(startRow + 2 + bonus.rows.length, c);
                if (summaryWs[totalRef])
                    summaryWs[totalRef].s = summaryRowStyle;
            }
            summaryWs['!cols'] = [{ wch: 30 }, { wch: 24 }, { wch: 22 }, { wch: 20 }];
        }
        else {
            summaryWs['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 16 }, { wch: 12 }];
        }
        XLSX.utils.book_append_sheet(workbook, summaryWs, 'Summary');
        // ── Sheet 2: Account Performance (one row per client) ──
        const apRows = accountPerformance.map((a) => {
            const hasTime = a.hours.seconds > 0;
            return [
                a.name,
                hasTime ? a.tasks.groups.map((g) => g.name).join(', ') || 'No task found' : 'No task found',
                money(a.revenue_8a),
                money(a.commissions),
                money(a.mrr),
                duration(a.hours.seconds),
                `${Number((a.hours.goal_hours || 0).toFixed(2))}h`,
                a.hours.percent_of_goal === null ? '-' : pct(a.hours.percent_of_goal),
                hasTime ? pct(a.time_percent) : 'N/A',
                hasTime ? pct(a.revenue_percent) : 'N/A',
                a.placements.total,
                a.placements.organic,
                a.placements.paid,
                a.placements.seo,
                a.beat.target_label || 'No',
                a.meeting_score ? `${a.meeting_score.score}/${a.meeting_score.out_of}` : '-',
            ];
        });
        const apTotals = accountPerformance.reduce((t, a) => ({
            revenue: t.revenue + (a.revenue_8a || 0),
            commissions: t.commissions + (a.commissions || 0),
            seconds: t.seconds + (a.hours.seconds || 0),
            goal: t.goal + (a.hours.goal_hours || 0),
            placements: t.placements + a.placements.total,
            organic: t.organic + a.placements.organic,
            paid: t.paid + a.placements.paid,
            seo: t.seo + a.placements.seo,
        }), { revenue: 0, commissions: 0, seconds: 0, goal: 0, placements: 0, organic: 0, paid: 0, seo: 0 });
        const apWs = sheetFrom(`Account Performance - ${titleSuffix}`, [
            'Client', 'Task Groups', 'G8A Revenue', 'Commissions', 'MRR', 'Total Hours', 'Goal Hours', '% of Goal',
            'Time %', 'Revenue %', 'Placements', 'Organic', 'Paid', 'SEO', 'Beat', 'Meeting Score',
        ], apRows, [
            'Total Summary', '', money(apTotals.revenue), money(apTotals.commissions), '', duration(apTotals.seconds),
            `${Number(apTotals.goal.toFixed(2))}h`, '', '', '', apTotals.placements, apTotals.organic, apTotals.paid, apTotals.seo, '', '',
        ]);
        XLSX.utils.book_append_sheet(workbook, apWs, 'Account Performance');
        // ── Sheet 3: Task Breakdown (the expanded view of every client) ──
        const taskRows = [];
        for (const a of accountPerformance) {
            for (const t of a.tasks.tags) {
                taskRows.push([a.name, t.name, t.group, duration(t.seconds), t.target_seconds === null ? '-' : taskHours(t.target_seconds)]);
            }
        }
        XLSX.utils.book_append_sheet(workbook, sheetFrom(`Task Breakdown - ${titleSuffix}`, ['Client', 'Task', 'Group', 'TTL', 'Goal'], taskRows), 'Task Breakdown');
        // ── Sheet 4: Accounts (Total Accounts list) ──
        const accRows = accounts.total.map((c) => [
            c.name,
            c.credit,
            money(c.management_fees),
            money(c.total_commissions),
            dateText(c.start_date).replace('Present', '-'),
            dateText(c.termination_date),
            c.days ?? '-',
        ]);
        XLSX.utils.book_append_sheet(workbook, sheetFrom(`Accounts - ${titleSuffix}`, ['Client', 'Credit', 'Mgmt Fees', 'Commissions', 'Start Date', 'End Date', 'Days'], accRows), 'Accounts');
        const safeName = name.replace(/[^\w]+/g, '_').replace(/^_|_$/g, '') || 'AM';
        XLSX.writeFile(workbook, `Employee_Performance_${safeName}_${month.replace('/', '_')}.xlsx`);
    }
    catch (error) {
        console.error('Failed to export:', error);
        toast.info('Failed to export. Please try again.');
    }
}
export default exportEmployeeDetails;
